import type { SuspendReason } from '@mogzo/shared';
import { twoFactorClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

/** The MOGZO API. Production default needs no build setting; override with `VITE_API_URL`. */
export const API_URL: string =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD ? 'https://api.mogzo.com' : 'http://localhost:3000');

/** Where people sign in. The session cookie belongs to the API, so signing in there covers this site too. */
export const PLAYER_SITE: string = import.meta.env.PROD
  ? 'https://mogzo.com'
  : 'http://localhost:5173';

const REQUEST_TIMEOUT_MS = 15_000;

/**
 * The account's own settings (M25: password, backup codes, signed-in browsers) and signing
 * out. Signing in happens on the player site.
 */
export const authClient = createAuthClient({
  baseURL: API_URL,
  basePath: '/auth',
  plugins: [twoFactorClient()],
});

/** better-auth's error, in MOGZO's words where the code is known. */
export function authErrorMessage(error: { code?: string; message?: string; status?: number }) {
  if (error.status === 429) return 'Too many attempts. Wait a minute and try again.';
  if (error.code === 'INVALID_PASSWORD') return 'That password isn’t right.';
  if (error.code === 'PASSWORD_TOO_SHORT') return 'Use at least 8 characters for your password.';
  if (error.code === 'PASSWORD_TOO_LONG') return 'Use at most 128 characters for your password.';
  return error.message || 'Something went wrong. Try again.';
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function readResponse<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Something went wrong. Try again.',
    );
  }
  return body as T;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(API_URL + path, {
    credentials: 'include',
    signal: signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  return readResponse<T>(response);
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(API_URL + path, {
    method,
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  return readResponse<T>(response);
}

export type Role = 'player' | 'developer' | 'moderator' | 'reviewer' | 'super_admin';

export interface Me {
  id: string;
  email: string;
  displayName: string;
  roles: Role[];
  twoFactorEnabled: boolean;
  profileComplete: boolean;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  status: 'in_development' | 'in_review' | 'live' | 'suspended';
  archivedAt: string | null;
  createdAt: string;
  /** The live version (M23), and one being checked or in review. */
  liveVersion: string | null;
  reviewVersion: string | null;
  suspendedAt: string | null;
  suspendReason: SuspendReason | null;
  suspendMessage: string | null;
  liveDataClearedAt: string | null;
}

export interface CliSession {
  id: string;
  deviceName: string;
  deviceSystem: string;
  lastIp: string | null;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

export interface CliCode {
  userCode: string;
  deviceName: string;
  deviceSystem: string;
  createdAt: string;
}

export interface Build {
  id: string;
  number: number;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  notes: string | null;
  version: string | null;
  zipBytes: number;
  unpackedBytes: number | null;
  fileCount: number | null;
  error: string | null;
  createdAt: string;
  readyAt: string | null;
  playUrl: string | null;
  /** Only the newest few builds keep their files; older ones can't be played any more. */
  filesDeleted: boolean;
}

export interface TesterInvite {
  id: string;
  url: string;
  uses: number;
  maxUses: number;
  expiresAt: string;
  status: 'active' | 'expired' | 'used_up' | 'turned_off';
}

export interface Tester {
  userId: string;
  name: string;
  joinedAt: string;
  postCount: number;
}

/** Sends a file as the request body (a listing picture), with its own type. */
export async function apiUpload<T>(path: string, file: Blob): Promise<T> {
  const response = await fetch(API_URL + path, {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(60_000),
    headers: { 'content-type': file.type },
    body: file,
  });
  return readResponse<T>(response);
}
